/**
 * Approvals — `/admin/approvals`.
 *
 * The inbox and the audit trail are the same screen because they are the same
 * record read twice. Two contract facts shape it.
 *
 * An inbox item carries `pending_task`: the caller's OWN open task at the
 * current step. That is what makes an item actionable, so the Approve/Reject
 * controls appear only where it is present — a request whose current step
 * belongs to someone else is readable but not decidable, and the screen shows
 * that rather than offering a button the server would refuse.
 *
 * The single-approval read leaves `pending_task` null on purpose and returns
 * the complete `tasks` and `events` arrays instead. So the detail panel draws
 * the trail from the detail read and never from the list row.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Share2, X } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { formatDateTime } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";
import { useUsers } from "@/features/identity/queries";

import {
  APPROVAL_STATUSES,
  decideApproval,
  delegateApproval,
  type Approval,
  type ApprovalFilters,
  type ApprovalStatus,
} from "../api";
import { platformKeys, useApproval, useApprovals } from "../queries";

const ALL = "__all__";

const STATUS_VARIANTS: Record<ApprovalStatus, "outline" | "success" | "destructive" | "secondary"> =
  {
    pending: "outline",
    approved: "success",
    rejected: "destructive",
    cancelled: "secondary",
  };

export interface ApprovalsPageProps {
  /**
   * The signed-in user. Segregation of duties is absolute in the substrate: a
   * requester cannot decide their own request whatever roles they hold, so the
   * screen must not offer them the control.
   */
  currentUserId?: string;
  canDecide?: boolean;
  canDelegate?: boolean;
  canReadUsers?: boolean;
}

export function ApprovalsPage({
  currentUserId,
  canDecide = false,
  canDelegate = false,
  canReadUsers = true,
}: ApprovalsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [mine, setMine] = useState(true);
  const [status, setStatus] = useState<string>("pending");
  const [openId, setOpenId] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [delegateTo, setDelegateTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const filters = useMemo<ApprovalFilters>(() => {
    const built: ApprovalFilters = {};
    if (mine) built.assignee = "me";
    if (status !== ALL) built.status = status as ApprovalStatus;
    return built;
  }, [mine, status]);

  const approvals = useApprovals(filters);
  const detail = useApproval(openId);
  const users = useUsers(canReadUsers);

  const userName = useMemo(() => {
    const byId = new Map((users.data ?? []).map((user) => [user.id, user.display_name]));
    return (userId: string | null): string =>
      userId === null ? "—" : (byId.get(userId) ?? userId.slice(0, 8));
  }, [users.data]);

  const when = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const rows = approvals.data ?? [];
  const openRow: Approval | undefined = rows.find((row) => row.id === openId);
  const ownRequest = openRow !== undefined && openRow.requested_by === currentUserId;
  // An open task at the caller's step is what makes a request decidable — and
  // never on one's own request.
  const actionable = ownRequest ? null : (openRow?.pending_task ?? null);

  const finish = async () => {
    await queryClient.invalidateQueries({ queryKey: platformKeys.approvals.all });
    setOpenId(null);
    setComment("");
    setDelegateTo("");
  };

  const decide = async (decision: "approve" | "reject") => {
    if (openId === null) return;
    setBusy(true);
    setActionError(null);
    try {
      await decideApproval(openId, decision, comment, newIdempotencyKey());
      await finish();
    } catch {
      setActionError(t("platform.approvals.decideError", "The decision could not be recorded."));
    } finally {
      setBusy(false);
    }
  };

  const delegate = async () => {
    if (openId === null || delegateTo === "") return;
    setBusy(true);
    setActionError(null);
    try {
      await delegateApproval(openId, delegateTo, comment, newIdempotencyKey());
      await finish();
    } catch {
      setActionError(t("platform.approvals.delegateError", "The task could not be delegated."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("platform.approvals.title", "Approvals")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "platform.approvals.subtitle",
            "Requests waiting on a decision, and the trail of every decision already made.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="approval-scope">{t("platform.approvals.scope", "Show")}</Label>
          <Select value={mine ? "me" : "all"} onValueChange={(value) => setMine(value === "me")}>
            <SelectTrigger id="approval-scope" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="me">{t("platform.approvals.mine", "Waiting on me")}</SelectItem>
              <SelectItem value="all">{t("platform.approvals.everyone", "Everyone's")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="approval-status">{t("platform.approvals.status", "Status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="approval-status" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {APPROVAL_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`platform.approvalStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {approvals.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {t("platform.approvals.empty", "Nothing is waiting for a decision here.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[52rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-3 text-start">
                      {t("platform.approvals.columns.request", "Request")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("platform.approvals.columns.requester", "Requested by")}
                    </th>
                    <th scope="col" className="p-3 text-end">
                      {t("platform.approvals.columns.amount", "Amount")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("platform.approvals.columns.step", "Step")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("platform.approvals.columns.status", "Status")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("platform.approvals.columns.raised", "Raised")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      onClick={() => setOpenId(row.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") setOpenId(row.id);
                      }}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                    >
                      <td className="p-3">
                        <span className="block">
                          {t(`platform.requestType.${row.request_type}`, row.request_type)}
                        </span>
                        <span dir="ltr" className="block font-mono text-xs text-muted-foreground">
                          {row.entity_type}
                        </span>
                      </td>
                      <td className="p-3">{userName(row.requested_by)}</td>
                      <td className="p-3 text-end">
                        {row.amount === null ? "—" : <MoneyDisplay value={row.amount} />}
                      </td>
                      <td className="p-3">
                        {row.pending_task === null
                          ? row.current_step
                          : t("platform.approvals.yourStep", "{{step}} · yours", {
                              step: row.current_step,
                            })}
                      </td>
                      <td className="p-3">
                        <Badge variant={STATUS_VARIANTS[row.status]}>
                          {t(`platform.approvalStatus.${row.status}`, row.status)}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground">{when(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={openId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setOpenId(null);
            setComment("");
            setDelegateTo("");
            setActionError(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {openRow === undefined
                ? t("platform.approvals.detailTitle", "Approval request")
                : t(`platform.requestType.${openRow.request_type}`, openRow.request_type)}
            </DialogTitle>
            <DialogDescription>
              {t(
                "platform.approvals.detailHint",
                "Every step, who acted, and what they said when they did.",
              )}
            </DialogDescription>
          </DialogHeader>

          {detail.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : detail.data === undefined ? (
            <p role="alert" className="text-sm text-destructive">
              {t("platform.approvals.detailError", "This request could not be loaded.")}
            </p>
          ) : (
            <div className="space-y-4 text-sm">
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
                <dt className="text-muted-foreground">
                  {t("platform.approvals.columns.requester", "Requested by")}
                </dt>
                <dd>{userName(detail.data.requested_by)}</dd>
                <dt className="text-muted-foreground">
                  {t("platform.approvals.columns.amount", "Amount")}
                </dt>
                <dd>
                  {detail.data.amount === null ? "—" : <MoneyDisplay value={detail.data.amount} />}
                </dd>
                <dt className="text-muted-foreground">
                  {t("platform.approvals.columns.status", "Status")}
                </dt>
                <dd>
                  <Badge variant={STATUS_VARIANTS[detail.data.status]}>
                    {t(`platform.approvalStatus.${detail.data.status}`, detail.data.status)}
                  </Badge>
                </dd>
                {detail.data.category === null ? null : (
                  <>
                    <dt className="text-muted-foreground">
                      {t("platform.approvals.category", "Category")}
                    </dt>
                    <dd>{detail.data.category}</dd>
                  </>
                )}
              </dl>

              <Card>
                <CardHeader>
                  <CardTitle>
                    <h3 className="text-sm font-medium">
                      {t("platform.approvals.trail", "Decision trail")}
                    </h3>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3">
                    {detail.data.events.map((event) => (
                      <li key={event.id} className="border-s-2 ps-3">
                        <p>
                          {t(`platform.approvalEvent.${event.type}`, event.type)}
                          {" · "}
                          <span className="text-muted-foreground">
                            {t("platform.approvals.stepN", "step {{step}}", { step: event.step })}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {userName(event.actor_id)} · {when(event.occurred_at)}
                        </p>
                        {event.comment === null ? null : (
                          <p className="mt-1 text-xs">{event.comment}</p>
                        )}
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {actionable === null ? (
                <p className="text-sm text-muted-foreground">
                  {ownRequest
                    ? t(
                        "platform.approvals.ownRequest",
                        "You raised this request, so somebody else has to decide it.",
                      )
                    : t(
                        "platform.approvals.notYours",
                        "This step is not assigned to you, so you cannot act on it.",
                      )}
                </p>
              ) : canDecide || canDelegate ? (
                <div className="space-y-3 border-t pt-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="approval-comment">
                      {t("platform.approvals.comment", "Comment (optional)")}
                    </Label>
                    <Textarea
                      id="approval-comment"
                      rows={2}
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                    />
                  </div>

                  {canDelegate ? (
                    <div className="space-y-1.5">
                      <Label htmlFor="approval-delegate">
                        {t("platform.approvals.delegateTo", "Hand over to")}
                      </Label>
                      <Select value={delegateTo} onValueChange={setDelegateTo}>
                        <SelectTrigger id="approval-delegate" className="w-64">
                          <SelectValue
                            placeholder={t("platform.approvals.pickUser", "Pick a colleague…")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {(users.data ?? [])
                            .filter((user) => user.id !== detail.data.requested_by)
                            .map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.display_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}

                  {actionError === null ? null : (
                    <p role="alert" className="text-sm text-destructive">
                      {actionError}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpenId(null)}>
              {t("actions.close", "Close")}
            </Button>
            {actionable !== null && canDelegate ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy || delegateTo === ""}
                onClick={() => void delegate()}
              >
                <Share2 className="size-4" aria-hidden="true" />
                {t("platform.approvals.delegate", "Delegate")}
              </Button>
            ) : null}
            {actionable !== null && canDecide ? (
              <>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={busy}
                  onClick={() => void decide("reject")}
                >
                  <X className="size-4" aria-hidden="true" />
                  {t("platform.approvals.reject", "Reject")}
                </Button>
                <Button type="button" disabled={busy} onClick={() => void decide("approve")}>
                  <Check className="size-4" aria-hidden="true" />
                  {t("platform.approvals.approve", "Approve")}
                </Button>
              </>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
